import { useCallback, useEffect, useRef, useState } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { serverUrl } from '@/api/client';
import { fetchAppRelease, type AppRelease } from '@/features/app-download/app-release';
import { cacheUpdate, installApk, isNative } from '@/native';
import {
  DISMISSED_KEY,
  LAST_CHECK_KEY,
  RELEASE_KEY,
  dueForCheck,
  parseVersionCode,
  readCachedRelease,
  shouldOfferUpdate,
} from './update-check';

/**
 * App 内检查更新（CR-067）。
 *
 * 客户原话「release apk包增加版本检查，自动更新的功能」——
 * 工人手机是公司统一发的，也有人用自己的，挨个通知「去网站重新下一个包」不现实。
 *
 * - **只在 App 里跑**：网页端天天都是最新的，没什么可更新的；
 * - **启动和回到前台各查一次**，最多 6 小时一次（`dueForCheck`）。工地弱网，查不到就当没有，不打扰；
 * - **比的是 versionCode**，不是版本名（那是 git SHA，没有大小）；
 * - 装的时候只能把系统安装界面弹出来，最后那下「安装」由工人自己点 —— 安卓不允许非商店应用静默替换自己。
 *
 * 覆盖安装不会丢东西：包名和签名都没变，登录状态、上传队列、草稿都还在（前提是这一版起就是正式签名包）。
 */

export type UpdateState = 'idle' | 'downloading' | 'installing' | 'needPermission' | 'failed';

export interface AppUpdate {
  /** 有新版本时才不为 null */
  release: AppRelease | null;
  state: UpdateState;
  /** 下载并拉起安装界面 */
  start: () => void;
  /** 这一版不再提示 */
  dismiss: () => void;
}

function readNumber(key: string): number | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    // 隐私模式 / 存储被清：当作没查过，大不了多查一次
    return null;
  }
}

function writeNumber(key: string, value: number): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    /* 存不下就每次都查，功能照常 */
  }
}

function readString(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeRelease(value: AppRelease | null): void {
  try {
    if (value) localStorage.setItem(RELEASE_KEY, JSON.stringify(value));
    else localStorage.removeItem(RELEASE_KEY);
  } catch {
    /* 存不下就退回「只在查得动的时候提示」，功能照常 */
  }
}

export function useAppUpdate(): AppUpdate {
  const [release, setRelease] = useState<AppRelease | null>(null);
  const [state, setState] = useState<UpdateState>('idle');
  // 同一时刻只查一次：启动那次还没回来，切前台又触发一次的话会查两遍
  const checking = useRef(false);

  const check = useCallback(async (force = false) => {
    if (!isNative || checking.current) return;
    checking.current = true;
    try {
      const info = await CapacitorApp.getInfo();
      const current = parseVersionCode(info.build);
      const dismissed = readNumber(DISMISSED_KEY);

      // 先把上次查到的结果摆出来，不等网络。节流管的是「发不发请求」，不是「记不记得有新版」——
      // 只记时间戳的话，冷启动 6 小时内一律直接返回，横幅就此消失（真机实测踩过）
      const cached = readCachedRelease(readString(RELEASE_KEY));
      if (shouldOfferUpdate(current, cached, dismissed)) setRelease(cached);

      if (!force && !dueForCheck(readNumber(LAST_CHECK_KEY), Date.now())) return;
      const found = await fetchAppRelease(serverUrl('/downloads/latest.json'));
      writeNumber(LAST_CHECK_KEY, Date.now());
      writeRelease(found);
      setRelease(shouldOfferUpdate(current, found, dismissed) ? found : null);
    } catch {
      // 没网、清单没传上去、插件不在：静默，上面摆出来的缓存结果留着。
      // 工地上这三样都常见，不能弹错误挡住打卡
    } finally {
      checking.current = false;
    }
  }, []);

  useEffect(() => {
    if (!isNative) return;
    // 冷启动必查一次（一份几百字节的清单，一次启动一个请求不算负担）。
    // 节流只管「回到前台」那一路 —— 连冷启动也挡掉的话，新版发出去了，
    // 工人重启 App 也要等六个小时才知道，这功能等于没有
    void check(true);

    // 回到前台再看一眼：手机在工地上一放一整天，不靠这一下就只有下次冷启动才知道有新版
    const handle = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) void check();
    });
    return () => {
      void handle.then((listener) => listener.remove());
    };
  }, [check]);

  // 这一版下载好的包放在哪。第一次更新要先去开「安装未知应用」的授权，回来再点一次 ——
  // 那一次不该再下 6MB（真机实测就是这么走的，工地上流量不便宜）。
  // 只在内存里记：App 被杀了就重下，无非多花一次流量
  const downloaded = useRef<{ versionCode: number; source: string } | null>(null);

  const start = useCallback(() => {
    if (!release) return;
    void (async () => {
      try {
        let source =
          downloaded.current?.versionCode === release.versionCode ? downloaded.current.source : null;
        if (!source) {
          setState('downloading');
          const response = await fetch(serverUrl(`/downloads/${release.file}`), { cache: 'no-store' });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          // 进 App 自己的缓存目录，不进「下载」文件夹：工人没要这个文件，
          // 而且「下载」那份的 content:// 授不了读权限给系统安装器（理由在 cacheUpdate 上）
          source = await cacheUpdate(await response.blob(), release.file);
          if (!source) throw new Error('not native');
          downloaded.current = { versionCode: release.versionCode, source };
        }
        setState((await installApk(source)) === 'settings' ? 'needPermission' : 'installing');
      } catch {
        // 缓存里那份可能已经被系统回收（插件报 NO_FILE）：忘掉它，下一次点就重下
        downloaded.current = null;
        setState('failed');
      }
    })();
  }, [release]);

  const dismiss = useCallback(() => {
    if (release) writeNumber(DISMISSED_KEY, release.versionCode);
    setRelease(null);
    setState('idle');
  }, [release]);

  return { release, state, start, dismiss };
}
